import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { ImportUsers, VerificationFlag } from '@finxone-platform/shared/sys-config-types';
import { Store } from '@ngxs/store';
import { Observable, Subject, catchError, map } from 'rxjs';
import { ConfigService } from '../config-service/config-service.service';

@Injectable({
  providedIn: 'root',
})
export class PasswordService {
  private baseUrl = '';

  verificationFlag: Subject<VerificationFlag> = new Subject();
  // import user observable
  importselecteduser: Subject<ImportUsers> = new Subject();

  private secureStore = new Map<string, string>();

  constructor(private http: HttpClient, private configService: ConfigService, private store: Store) {
    this.configService.getApi('metadata_service').subscribe((api) => {
      this.baseUrl = api;
    });
  }

  public resetPassword(
    currentPassword: string,
    newPassword: string,
    confirmPassword: string,
  ): Observable<boolean> {
    return this.http
      .post(
        this.baseUrl + '/users/reset-password',
        {
          currentPassword,
          newPassword,
          confirmPassword,
        },
        { responseType: 'text' },
      )
      .pipe(
        map(() => {
          return true;
        }),
        catchError<boolean, Observable<boolean>>((_err) => {
          console.error(`Error verifying otp: ${JSON.stringify(_err)}`);
          throw _err;
        }),
      );
  }

  public resetPasswordEmailOnly(email: string): Observable<boolean> {
    return this.http
      .post(
        this.baseUrl + '/users/reset-password-email',
        {
          email,
        },
        { responseType: 'text' },
      )
      .pipe(
        map(() => {
          return true;
        }),
        catchError<boolean, Observable<boolean>>((_err) => {
          console.error(`Error with sending email: ${JSON.stringify(_err)}`);
          throw _err;
        }),
      );
  }

  public updatePassword(token: string, newPassword: string, confirmPassword: string): Observable<boolean> {
    return this.http
      .post(
        this.baseUrl + '/users/change-credential',
        {
          token,
          newPassword,
          confirmPassword,
        },
        { responseType: 'text' },
      )
      .pipe(
        map(() => {
          return true;
        }),
        catchError<boolean, Observable<boolean>>((_err) => {
          console.error(`Error changing password: ${JSON.stringify(_err)}`);
          throw _err;
        }),
      );
  }

  public forgotPasswordEmail(): Observable<boolean> {
    return this.http.put(this.baseUrl + '/users/reset-password-email', '').pipe(
      map(() => {
        return true;
      }),
      catchError<boolean, Observable<boolean>>((_err) => {
        console.error(`Error with sending email: ${JSON.stringify(_err)}`);
        throw _err;
      }),
    );
  }

  /**
   * Store a sensitive value by key
   */
  public set(key: string, value: string): void {
    this.secureStore.set(key, value);
  }

  /**
   * Retrieve value by key (one-time read)
   */
  public get(key: string): string | null {
    const value = this.secureStore.get(key) ?? null;
    this.secureStore.delete(key);
    return value;
  }

  /**
   * Retrieve all values at once (one-time read)
   */
  public consumeAll(): Partial<Record<string, string>> {
    const result: Partial<Record<string, string>> = {};

    this.secureStore.forEach((value, key) => {
      result[key] = value;
    });

    this.clear();
    return result;
  }

  /**
   * Clear all stored values
   */
  private clear(): void {
    this.secureStore.clear();
  }
}
